import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n'; // Inicializar i18n antes de renderizar la app
import App from './App';
import { PinProvider } from './contexts/PinContext';
import { AiDevicePolicyProvider } from './contexts/AiDevicePolicyContext';
import { clearAllCaches } from './utils/cacheCleaner';
import {
  canSafelyReloadForSwUpdate,
  markRecoverSessionAfterReload,
} from './utils/sessionFlags';

// OPTIMIZACIÓN 1: Inicialización temprana del WebSocket ANTES de montar componentes
(function initWebSocketEarly() {
  const existingToken = localStorage.getItem('token');
  if (existingToken && typeof window !== 'undefined') {
    // El WebSocket se conectará en el useEffect de App.tsx
  }
})();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <PinProvider>
      <AiDevicePolicyProvider>
        <App />
      </AiDevicePolicyProvider>
    </PinProvider>
  </React.StrictMode>
);

// ChunkLoadError: recargar cuando falla un chunk viejo tras un deploy
window.addEventListener('error', (event) => {
  const error = event.error;
  if (error && error.name === 'ChunkLoadError') {
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => { caches.delete(name); });
      });
    }
    setTimeout(() => {
      markRecoverSessionAfterReload();
      window.location.reload();
    }, 100);
  }
});

window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  if (error && error.name === 'ChunkLoadError') {
    event.preventDefault();
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => { caches.delete(name); });
      });
    }
    setTimeout(() => {
      markRecoverSessionAfterReload();
      window.location.reload();
    }, 100);
  }
});

// Service Worker real (incluye handler push).
// En desarrollo nginx sirve public/service-worker.js (NO el no-op vacío de CRA).
if ('serviceWorker' in navigator) {
  (async () => {
    try {
      // En dev: limpiar solo Cache API, sin desregistrar el SW.
      if (process.env.NODE_ENV === 'development') {
        await clearAllCaches();
      }

      const swPath = window.location.pathname.startsWith('/pwa/')
        ? '/pwa/service-worker.js'
        : '/service-worker.js';
      const scope = window.location.pathname.startsWith('/pwa/') ? '/pwa/' : '/';

      const registration = await navigator.serviceWorker.register(swPath, {
        updateViaCache: 'none',
        scope,
      });

      let hasReloadedForUpdate = false;
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
              if (
                process.env.NODE_ENV === 'production'
                && canSafelyReloadForSwUpdate()
                && !hasReloadedForUpdate
              ) {
                hasReloadedForUpdate = true;
                markRecoverSessionAfterReload();
                window.location.reload();
              }
            }
          });
        }
      });

      const safeUpdate = () => {
        void Promise.resolve()
          .then(() => registration.update())
          .catch(() => { /* registration invalidado / en transición */ });
      };

      if (process.env.NODE_ENV === 'production') {
        setInterval(safeUpdate, 3600000);
      } else {
        // Dar tiempo a que el SW termine install/activate antes de update.
        setTimeout(safeUpdate, 1500);
      }
    } catch {
      // Si falla el registro, la app sigue; push pedirá registro al activar.
    }
  })();
}
